import React, { useState, useRef, useEffect } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface } from "@/components/app/Primitives";
import { Sparkles, Send, TicketCheck, FileText } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
    "What's my leave balance?",
    "When is the next payday?",
    "What are the upcoming holidays?",
    "What's the company leave policy?",
];

export default function AIAssistant() {
    const [messages, setMessages] = useState([
        { role: "ai", text: "Hi! I'm your HR assistant. Ask me about your leave balance, payroll schedule, company policies, benefits, and more." },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const endRef = useRef(null);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const ask = async (q) => {
        const question = (q ?? input).trim();
        if (!question || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text: question }]);
        setBusy(true);
        try {
            const { data } = await api.post("/ai/ask", { question });
            setMessages((m) => [...m, {
                role: "ai", text: data.answer, citations: data.citations || [],
                canTicket: data.can_create_ticket, suggestedTicket: data.suggested_ticket,
                conversationId: data.conversation_id, usedLlm: data.used_llm,
            }]);
        } catch (e) {
            const status = e.response?.status;
            const msg = status === 402 ? "The AI HR Agent isn't licensed for your company yet — contact your administrator."
                : status === 404 ? "The AI HR Agent module isn't enabled for your company."
                : formatErrorDetail(e.response?.data?.detail) || "Something went wrong.";
            setMessages((m) => [...m, { role: "ai", text: msg }]);
        } finally { setBusy(false); }
    };

    const createTicket = async (msg) => {
        try {
            const t = msg.suggestedTicket || { subject: "HR request via AI Agent", category: "hr", priority: "normal", description: "" };
            const { data } = await api.post("/ai/tickets", { ...t, conversation_id: msg.conversationId });
            toast.success(`Ticket created (#${(data.id || "").slice(0, 8)}) and routed to HR`);
            setMessages((m) => [...m, { role: "ai", text: `I've created a ticket for you and routed it to the HR team. They'll follow up.` }]);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Couldn't create ticket"); }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)]">
            <PageHeader title={<span className="flex items-center gap-2"><Sparkles className="size-5 text-primary" /> HR Assistant</span>}
                subtitle="Ask about leave, payroll, policies and benefits. Answers use your company's HR data and approved documents." />
            <Surface className="flex-1 overflow-auto p-4 space-y-3">
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-white" : "bg-secondary"}`} data-testid={`msg-${m.role}`}>
                            {m.text}
                            {!!m.citations?.length && (
                                <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                                    <div className="text-[11px] uppercase font-semibold text-muted-foreground flex items-center gap-1"><FileText className="size-3" /> Sources</div>
                                    {m.citations.map((c) => (
                                        <div key={c.document_id} className="text-xs text-muted-foreground">• {c.title} <span className="opacity-70">v{c.version}{c.effective_date ? ` · eff. ${c.effective_date}` : ""}</span></div>
                                    ))}
                                </div>
                            )}
                            {m.canTicket && (
                                <button onClick={() => createTicket(m)} className="mt-2 h-7 px-2.5 rounded-md bg-white text-primary border border-primary/30 text-xs inline-flex items-center gap-1" data-testid="ai-create-ticket">
                                    <TicketCheck className="size-3.5" /> Create a ticket
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {busy && <div className="text-xs text-muted-foreground">Assistant is thinking…</div>}
                <div ref={endRef} />
            </Surface>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => <button key={s} onClick={() => ask(s)} className="text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-secondary/70">{s}</button>)}
            </div>
            <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask a question…" className="flex-1 h-11 px-4 rounded-md border border-border" data-testid="ai-input" />
                <button type="submit" disabled={busy} className="h-11 px-4 rounded-md bg-primary text-white flex items-center gap-2 disabled:opacity-50" data-testid="ai-send"><Send className="size-4" /> Send</button>
            </form>
        </div>
    );
}
